/**
 * localStorage wrapper ที่ encrypt/decrypt ข้อมูลด้วย AES (crypto-js)
 * - ข้อมูลเก่าที่ยังไม่ encrypt (ไม่มี prefix ENC:) จะอ่านได้ตามเดิม (backward compatible)
 * - ข้อมูลใหม่ที่ set ผ่าน setItem จะถูก encrypt ทุกครั้ง
 * - ใช้ผ่าน getItem / setItem / removeItem แทน localStorage โดยตรง
 * - Secret: REACT_APP_STORAGE_SECRET หรือ default 'cliniter dental'
 */
import CryptoJS from 'crypto-js'

const ENCRYPTION_PREFIX = 'ENC:'
const SECRET = process.env.REACT_APP_STORAGE_SECRET || 'cliniter dental'

function encrypt(plainText: string): string {
  try {
    return CryptoJS.AES.encrypt(plainText, SECRET).toString()
  } catch {
    return plainText
  }
}

function decrypt(cipherText: string): string {
  try {
    const bytes = CryptoJS.AES.decrypt(cipherText, SECRET)
    const str = bytes.toString(CryptoJS.enc.Utf8)
    return str || ''
  } catch {
    return ''
  }
}

/**
 * เก็บค่าใน localStorage (รองรับ string, number, object จะถูก JSON.stringify ก่อน encrypt)
 */
export function setItem(key: string, value: string | number | Record<string, unknown> | unknown[] | null | undefined): void {
  if (value === null || value === undefined) {
    localStorage.removeItem(key)
    return
  }
  const str = typeof value === 'object' ? JSON.stringify(value) : String(value)
  const encrypted = encrypt(str)
  localStorage.setItem(key, ENCRYPTION_PREFIX + encrypted)
}

/**
 * อ่านค่าจาก localStorage (ถ้าเป็นข้อมูลที่ encrypt ไว้จะ decrypt ให้)
 * รองรับ: (1) prefix ENC: (format ใหม่) (2) ค่า encrypt แบบเก่าไม่มี prefix (3) plain text
 * คืนค่าเป็น string เสมอ (ถ้าเดิมเก็บเป็น object ให้เรียก JSON.parse เอง)
 */
export function getItem(key: string): string | null {
  const raw = localStorage.getItem(key)
  if (raw === null) return null
  if (raw.startsWith(ENCRYPTION_PREFIX)) {
    const decrypted = decrypt(raw.slice(ENCRYPTION_PREFIX.length))
    return decrypted === '' ? null : decrypted
  }
  const tryDecrypt = decrypt(raw)
  if (tryDecrypt !== '') return tryDecrypt
  return raw
}

/**
 * ลบ key ออกจาก localStorage
 */
export function removeItem(key: string): void {
  localStorage.removeItem(key)
}

export const secureStorage = {
  setItem,
  getItem,
  removeItem
}
